import apiFetch from '../../../utils/apiFetch';
import { rollDiceWithBox } from '../../../utils/diceBoxManager';
import { hasHeroicInspiration, heroicDieChoices, heroicDieRerollDecision, heroicRerollResult, withRerolledDie } from '../../../shared/rules/heroicInspiration.mjs';
import { notify } from '../../../utils/notification';
import { withHalflingLuck } from './halflingLuck';

/**
 * Spends Heroic Inspiration for one d20 test made on the sheet (`kind` attack, check or save) with the new die in
 * `rolls`; the same requestId replays and returns the stored die.
 */
export const claimHeroicInspiration = async (characterId, { requestId, kind = 'attack', rolls, sides }) => {
  const response = await apiFetch(`/characters/${encodeURIComponent(characterId)}/heroic-inspiration`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ requestId, kind, ...(rolls ? { rolls } : {}), ...(sides ? { sides } : {}) }),
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(result.message || 'Heroic Inspiration could not be used.');
  return result;
};

/** The character with Heroic Inspiration's new state, unless it already holds a newer one. */
export const withHeroicInspiration = (character, heroicInspiration) => (character && heroicInspiration
  && Number(heroicInspiration.revision || 0) >= Number(character.heroicInspiration?.revision || 0)
  ? { ...character, heroicInspiration } : character);

const rollD20 = async (prepareRoll) => {
  await prepareRoll?.('Heroic Inspiration');
  const roll = await rollDiceWithBox([{ count: 1, sides: 20 }]).catch(() => null);
  const value = Number(roll?.rolls?.[0]?.[0]);
  return Number.isInteger(value) && value >= 1 && value <= 20 ? value : Math.floor(Math.random() * 20) + 1;
};

/**
 * Rerolls one d20 of a test made on the sheet with Heroic Inspiration: rolls the new die (Halfling Luck rerolls a 1),
 * spends Heroic Inspiration on the server with it, and returns the new kept d20 (`heroicRerollResult`: the lower die
 * is rerolled under Advantage or Disadvantage) and the new die. Disadvantage without both dice rolls a second new die.
 * `prepareRoll` opens the dice display, so the new die is seen rolling.
 */
export async function rerollWithHeroicInspiration({ character, characterId, requestId, kind, rollMode = 'normal', kept, dice = null, onCharacterChange, prepareRoll }) {
  const { d20s: [newDie] } = await withHalflingLuck(character, [await rollD20(prepareRoll)], { label: 'Heroic Inspiration reroll' });
  const claimed = await claimHeroicInspiration(characterId, { requestId, kind, rolls: [newDie] });
  onCharacterChange?.(previous => withHeroicInspiration(previous, claimed.heroicInspiration));
  // A replayed claim keeps the die stored with the first one.
  const die = Number.isInteger(claimed.heroicInspiration?.rolls?.[0]) ? claimed.heroicInspiration.rolls[0] : newDie;
  const result = heroicRerollResult({ rollMode, kept, dice, newDie: die });
  return { newDie: die, d20: result ?? Math.min(die, await rollD20(prepareRoll)) };
}

/**
 * Heroic Inspiration on damage or healing dice (`groups`: [{ sides, values }]) made on the sheet: when a die landed in its
 * lower half, `choose(decision)` asks which one to reroll; the new die is rolled, spent on the server with it and swapped
 * in. Returns the groups to use (unchanged when declined, not offered or refused).
 */
export async function offerHeroicDieReroll({ character, characterId, requestId, kind, what, groups, choose, onCharacterChange, prepareRoll }) {
  if (character?.entityType === 'enemy' || !hasHeroicInspiration(character)) return groups;
  const choices = heroicDieChoices(groups);
  if (!choices.length) return groups;
  const answer = await choose(heroicDieRerollDecision(requestId, what, choices));
  const picked = choices.find(choice => choice.id === answer?.choiceId);
  if (!picked) return groups;
  try {
    await prepareRoll?.('Heroic Inspiration');
    const roll = await rollDiceWithBox([{ count: 1, sides: picked.sides }]).catch(() => null);
    const rolled = Number(roll?.rolls?.[0]?.[0] ?? roll?.rolls?.[0]);
    const fresh = Number.isInteger(rolled) && rolled >= 1 && rolled <= picked.sides ? rolled : Math.floor(Math.random() * picked.sides) + 1;
    const claimed = await claimHeroicInspiration(characterId, { requestId, kind, sides: picked.sides, rolls: [fresh] });
    onCharacterChange?.(previous => withHeroicInspiration(previous, claimed.heroicInspiration));
    // A replayed claim keeps the die stored with the first one.
    const die = Number.isInteger(claimed.heroicInspiration?.rolls?.[0]) ? claimed.heroicInspiration.rolls[0] : fresh;
    notify(`Heroic Inspiration\nRerolled a d${picked.sides} (${picked.value} → ${die}).`, 'success');
    return withRerolledDie(groups, picked, die);
  } catch (error) { notify(error.message, 'warning'); return groups; }
}
